import React, { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, AlertCircle, Send, Receipt, FileInput, Clock3, AlarmClock } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import {
  pushSupported, pushPermission, subscribeToPush,
  unsubscribeFromPush, getCurrentSubscription, sendTestPush,
  getPreferences, updatePreferences,
} from "../lib/push";

const CATEGORY_META = {
  invoice_paid: {
    icon: Receipt, color: "text-emerald-300",
    ca: { title: "Factura emesa cobrada", desc: "Quan un client marca o paga una factura emesa." },
    es: { title: "Factura emitida cobrada", desc: "Cuando un cliente marca o paga una factura emitida." },
  },
  invoice_received: {
    icon: FileInput, color: "text-blue-300",
    ca: { title: "Nova factura rebuda", desc: "Quan una factura nova arriba per OCR, email o bústia." },
    es: { title: "Nueva factura recibida", desc: "Cuando una factura nueva llega por OCR, email o buzón." },
  },
  trial_ending: {
    icon: Clock3, color: "text-amber-300",
    ca: { title: "Trial a punt d'acabar", desc: "Avís 3 dies abans que caduqui la prova gratuïta." },
    es: { title: "Trial a punto de acabar", desc: "Aviso 3 días antes de que caduque la prueba gratuita." },
  },
  payment_reminder: {
    icon: AlarmClock, color: "text-purple-300",
    ca: { title: "Recordatori de cobrament", desc: "Quan una factura supera els 30 dies sense cobrar." },
    es: { title: "Recordatorio de cobro", desc: "Cuando una factura supera los 30 días sin cobrar." },
  },
};

export default function PushNotificationsCard() {
  const { lang } = useI18n();
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [categories, setCategories] = useState([]);
  const [prefBusy, setPrefBusy] = useState(null);
  const tr = (ca, es) => (lang === "es" ? es : ca);

  const loadPrefs = async () => {
    try {
      const r = await getPreferences();
      setPrefs(r.prefs);
      setCategories(r.categories);
    } catch { /* not authenticated yet, ignore */ }
  };

  const refresh = async () => {
    const sup = pushSupported();
    setSupported(sup);
    setPermission(pushPermission());
    if (sup) {
      try {
        const sub = await getCurrentSubscription();
        setSubscribed(!!sub);
      } catch {
        setSubscribed(false);
      }
    }
  };

  useEffect(() => {
    refresh();
    loadPrefs();
  }, []);

  const togglePref = async (cat) => {
    const next = { ...prefs, [cat]: !prefs[cat] };
    setPrefs(next);
    setPrefBusy(cat);
    try {
      await updatePreferences(next);
    } catch (e) {
      // Revert on failure
      setPrefs(prefs);
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setPrefBusy(null);
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success(tr("Notificacions activades", "Notificaciones activadas"));
      await refresh();
    } catch (e) {
      if (e.code === "permission-denied") {
        toast.error(tr(
          "Permís denegat. Habilita'l a la configuració del navegador.",
          "Permiso denegado. Habilítalo en la configuración del navegador."
        ));
      } else if (e.message === "push-server-disabled") {
        toast.error(tr(
          "El servidor encara no té les notificacions configurades.",
          "El servidor aún no tiene las notificaciones configuradas."
        ));
      } else {
        toast.error(e.message || "Error");
      }
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      toast.success(tr("Notificacions desactivades",
                       "Notificaciones desactivadas"));
      await refresh();
    } catch (e) {
      toast.error(e.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await sendTestPush();
      if (r.sent > 0) {
        toast.success(tr(
          `Enviada a ${r.sent} dispositiu${r.sent === 1 ? "" : "s"}`,
          `Enviada a ${r.sent} dispositivo${r.sent === 1 ? "" : "s"}`));
      } else {
        toast.message(tr("Notificació enviada", "Notificación enviada"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setTesting(false);
    }
  };

  if (!supported) {
    return (
      <div className="card-surface p-5" data-testid="push-card">
        <div className="flex items-center gap-2 mb-2">
          <BellOff size={14} className="text-slate-400" />
          <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
            {tr("Notificacions push", "Notificaciones push")}
          </h2>
        </div>
        <p className="text-xs text-slate-500 flex items-start gap-2">
          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
          {tr(
            "El teu navegador no permet notificacions web. Si fas servir iPhone, instal·la l'app a la pantalla d'inici primer.",
            "Tu navegador no permite notificaciones web. Si usas iPhone, instala la app en la pantalla de inicio primero."
          )}
        </p>
      </div>
    );
  }

  const blocked = permission === "denied";

  return (
    <div className="card-surface p-5" data-testid="push-card">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {subscribed
          ? <Bell size={14} className="text-emerald-300" />
          : <BellOff size={14} className="text-slate-400" />}
        <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
          {tr("Notificacions push", "Notificaciones push")}
        </h2>
        {subscribed && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 font-semibold">
            {tr("Actives", "Activas")}
          </span>
        )}
      </div>

      <p className="text-xs text-slate-400 leading-relaxed mb-4">
        {tr(
          "Rep avisos al teu dispositiu quan: una factura emesa es paga, una factura rebuda arriba, o el teu trial està a punt d'acabar.",
          "Recibe avisos en tu dispositivo cuando: una factura emitida se paga, una factura recibida llega, o tu trial está a punto de acabar."
        )}
      </p>

      {blocked && (
        <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 mb-3 flex items-start gap-2"
             data-testid="push-blocked">
          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
          {tr(
            "Has bloquejat les notificacions en aquest navegador. Per activar-les, ves a la configuració del lloc al teu navegador i permet les notificacions.",
            "Has bloqueado las notificaciones en este navegador. Para activarlas, ve a la configuración del sitio en tu navegador y permite las notificaciones."
          )}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {!subscribed ? (
          <Button
            onClick={enable}
            disabled={busy || blocked}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="push-enable">
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" />
              : <Bell size={14} className="mr-2" />}
            {tr("Activar notificacions", "Activar notificaciones")}
          </Button>
        ) : (
          <>
            <Button
              onClick={test}
              disabled={testing}
              variant="outline"
              className="bg-blue-500/15 border-blue-500/40 text-blue-100 hover:bg-blue-500/25"
              data-testid="push-test">
              {testing ? <Loader2 size={14} className="mr-2 animate-spin" />
                : <Send size={14} className="mr-2" />}
              {tr("Provar notificació", "Probar notificación")}
            </Button>
            <Button
              onClick={disable}
              disabled={busy}
              variant="outline"
              className="bg-white/5 border-white/10 text-slate-300 hover:bg-rose-500/15 hover:border-rose-500/40 hover:text-rose-200"
              data-testid="push-disable">
              {busy ? <Loader2 size={14} className="mr-2 animate-spin" />
                : <BellOff size={14} className="mr-2" />}
              {tr("Desactivar", "Desactivar")}
            </Button>
          </>
        )}
      </div>

      {/* Per-category preferences */}
      {subscribed && prefs && categories.length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/5"
             data-testid="push-preferences">
          <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
            {tr("Què vols rebre", "Qué quieres recibir")}
          </h3>
          <div className="space-y-2">
            {categories.map((cat) => {
              const meta = CATEGORY_META[cat];
              if (!meta) return null;
              const Icon = meta.icon;
              const labels = lang === "es" ? meta.es : meta.ca;
              const enabled = prefs[cat] !== false;
              return (
                <div
                  key={cat}
                  className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:bg-white/[0.04]"
                  data-testid={`push-pref-${cat}`}>
                  <div className={`w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-200 font-medium">
                      {labels.title}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                      {labels.desc}
                    </p>
                  </div>
                  <div className="flex items-center pt-1 flex-shrink-0">
                    {prefBusy === cat ? (
                      <Loader2 size={14} className="animate-spin text-slate-400" />
                    ) : (
                      <Switch
                        checked={enabled}
                        onCheckedChange={() => togglePref(cat)}
                        data-testid={`push-pref-toggle-${cat}`} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
